/**
 * Resume Job Guard — Crash loop detection for native iOS WebView restarts.
 *
 * Tracks how many times the app restarts on /resume-job/:id within a window.
 * If it exceeds MAX_RESTARTS in WINDOW_MS, signals the startup overlay to
 * redirect to /dashboard instead of preserving the crashing route.
 *
 * Uses localStorage (persists across WebView process terminations within same app).
 */

const STORAGE_KEY = "tm_resume_restart_guard";
const MAX_RESTARTS = 2;
const WINDOW_MS = 60_000; // 60 seconds

interface GuardState {
  count: number;
  firstTs: number;
  route: string;
}

function readState(): GuardState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as GuardState;
  } catch {
    return null;
  }
}

function writeState(state: GuardState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

/**
 * Called by NativeStartupOverlay when the app starts on a /resume-job route.
 * Returns true if the crash loop threshold has been exceeded (should redirect to /dashboard).
 *
 * 1.72: when a valid active workflow exists, repeated reloads are camera/OS
 * driven WebView restarts — the restart is still recorded but the user is
 * NEVER ejected from the appraisal. Loop protection still applies when there
 * is no active workflow.
 */
export function checkResumeJobCrashLoop(route: string, hasActiveWorkflow = false): boolean {
  if (hasActiveWorkflow) {
    const now = Date.now();
    const s = readState();
    const fresh = !s || s.route !== route || now - s.firstTs > WINDOW_MS;
    writeState(fresh ? { count: 1, firstTs: now, route } : { ...s!, count: s!.count + 1 });
    return false;
  }

  const now = Date.now();
  const state = readState();

  if (!state || state.route !== route || (now - state.firstTs) > WINDOW_MS) {
    // First restart or window expired — start fresh counter
    writeState({ count: 1, firstTs: now, route });
    console.log("[ResumeGuard] Recording restart 1 for", route);
    return false;
  }

  const newCount = state.count + 1;
  writeState({ ...state, count: newCount });
  console.log(`[ResumeGuard] Recording restart ${newCount} for ${route} (window: ${now - state.firstTs}ms)`);

  if (newCount > MAX_RESTARTS) {
    console.warn(`[ResumeGuard] Crash loop detected! ${newCount} restarts in ${now - state.firstTs}ms — bailing to /dashboard`);
    return true;
  }

  return false;
}

/**
 * Called when the appraisal screen renders successfully — resets the guard.
 */
export function clearResumeJobCrashLoop() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log("[ResumeGuard] Cleared — appraisal rendered successfully");
  } catch {}
}
